
import Themeable from 'vuetify/lib/mixins/themeable'
import { Component, Vue, Watch } from 'vue-property-decorator'
import { DataTableHeader } from 'vuetify'
import DNSRepositoryInterface from '@/services/DNS/Interfaces';
import DeleteDnsZoneDialog from '@/components/dialogs/dns/DeleteDnsZoneDialog.vue'
import DepartmentRepositoryInterface, { DepartmentInterface } from '../../../services/Departments/Interfaces';
import { AxiosError } from 'axios';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  mixins: [Themeable],
  inject: ['dnsRepository', 'departmentRepository'],
  components: { DeleteDnsZoneDialog, PageHeader },
})
export default class DNSDepartments extends Vue {
  public loading: boolean = true;

  public headers: DataTableHeader[] = [
    { text: 'Name', value: 'name', width: 100 },
    { text: 'ID', value: '_id', width: 100 },
    { text: '', value: '', width: 100, sortable: false },
    { text: 'Actions', value: 'actions', width: 50, sortable: false },
  ];

  public departments: DepartmentInterface[] = [];
  public options: ListPaginationOptions = defaultListOptions();
  public readonly LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public readonly dnsRepository!: DNSRepositoryInterface;
  
  public readonly departmentRepository!: DepartmentRepositoryInterface;

  public promoting: boolean = false;

  public promotionDepartmentTarget: string = '';

  public demoting: boolean = false;

  public demoteDepartmentTarget: DepartmentInterface = {} as DepartmentInterface;

  public existingDepartments: DepartmentInterface[] = [];

  public async created(): Promise<void> {
    this.applyRouteQuery();
    console.log('hello');
    await this.initialize();
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      const parsed = parseListQuery(this.$route.query);
      this.options.page = parsed.page;
      this.options.itemsPerPage = parsed.itemsPerPage;
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({
        name: 'dns_departments',
        query: buildListPaginationQuery(this.options),
      });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  @Watch('options.page')
  @Watch('options.itemsPerPage')
  public async onPaginationChange(): Promise<void> {
    await this.syncRouteQuery();
  }

  @Watch('$route')
  public onRouteChange(): void {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'dns_departments') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }
    this.applyRouteQuery();
  }

  private async initialize(): Promise<void> {
    this.loading = true;
    const dnsDepartments = await this.dnsRepository.getDepartments();
    if (dnsDepartments.length > 0) {
        this.departments = await this.departmentRepository.getDepartmentsByIds(dnsDepartments)
    } else {
        this.departments = [];
    }

    this.loading = false;
  }

  private demoteDepartmentPrompt(department: DepartmentInterface): void {
    this.demoting = true;
    this.demoteDepartmentTarget = department;
  }

  private demoteCancel(): void {
    this.demoting = false;
    this.demoteDepartmentTarget = {} as DepartmentInterface;
  }

  private async demoteDepartment(): Promise<void> {
    try {
        await this.dnsRepository.demoteDepartment((this.demoteDepartmentTarget as DepartmentInterface)._id);
        this.$successDialog.show(`Department '${this.demoteDepartmentTarget.name}' has been demoted.`)
        await this.initialize();
    } catch (err) {
        console.error(err);
        this.$errorDialog.show('Failed to demote department.');
    } finally {
        this.demoting = false;
        this.demoteDepartmentTarget = {} as DepartmentInterface;
    }
  }

  private async promoteDepartmentPrompt(department: DepartmentInterface): Promise<void> {
    this.existingDepartments = await this.departmentRepository.getAllDepartments();
    this.promoting = true;
  }

  private async promoteDepartment(): Promise<void> {
    try {
        await this.dnsRepository.promoteDepartment(this.promotionDepartmentTarget);
        await this.initialize();
    } catch (err) {
        if (err instanceof Error && (err as AxiosError).response?.status === 409) {
            this.$errorDialog.show('Department is already promoted.');
        } else {
            console.error(err)
            this.$errorDialog.show('Failed to promote department.');
        }
    } finally {
        this.promoting = false;
        this.promotionDepartmentTarget = '';
    }
  }

  private promotionCancel(): void {
    this.promoting = false;
    this.promotionDepartmentTarget = '';
  }
}
